import React from 'react';
import { Tooltip, TooltipTrigger, TooltipContent } from '../ui/tooltip';

// Shared by the Pre-release Benchmark scorecard and checkpoint table: metric
// formatting and the hover-able multiple of a benchmark group's average.

function fmtInt(v) { return Number(v).toLocaleString('en-GB', { maximumFractionDigits: 0 }); }
function fmtEur(v) { return `€${fmtInt(v)}`; }
function fmtCompact(v) { return Number(v).toLocaleString('en-GB', { notation: 'compact', maximumFractionDigits: 1 }); }
export function metricFormatters(metric) {
  // Counts (players, bets) as plain numbers; everything else is in EUR.
  return metric === 'players' || metric === 'n_bets'
    ? { fmt: fmtInt, axisFmt: fmtCompact }
    : { fmt: fmtEur, axisFmt: v => `€${fmtCompact(v)}` };
}

const MAX_LISTED_MEMBERS = 10;

function fmtShortDay(v) {
  return new Date(v).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
}

// The games a benchmark group holds at the compared day, highest first, with
// the group's average — shown on hover over a multiple.
function GroupList({ group, fmt, age }) {
  const listed = group.members.slice(0, MAX_LISTED_MEMBERS);
  return (
    <div className="text-[11px] min-w-[260px]">
      <p className="font-bold text-ink mb-1">
        {group.n} game{group.n === 1 ? '' : 's'} at day {age}
        {group.mean != null && <span className="text-ink-dim font-medium"> · average {fmt(group.mean)}</span>}
      </p>
      {listed.map(m => (
        <div key={m.unique_game_id} className="flex items-center gap-2">
          <span className="text-ink truncate max-w-[160px]">{m.game_title}</span>
          <span className="text-ink-mute">{m.provider} · {m.category} · {fmtShortDay(m.release_date)}</span>
          <span className="text-ink ml-auto pl-2">{fmt(m.value)}</span>
        </div>
      ))}
      {group.n > listed.length && <p className="text-ink-mute mt-1">+ {group.n - listed.length} more</p>}
      {group.n < 3 && <p className="text-warning mt-1">Only {group.n} game{group.n === 1 ? '' : 's'} — read this comparison with care.</p>}
    </div>
  );
}

// A multiple of a benchmark group's average, with the group's size;
// hovering lists the group's games.
export function Multiple({ value, group, fmt, age }) {
  const content = value == null ? (
    <span className="text-ink-mute">—{group.n > 0 ? ` (${group.n})` : ''}</span>
  ) : (
    <span className="whitespace-nowrap">
      <span className={`font-bold ${value >= 1 ? 'text-positive' : 'text-negative'}`}>{value.toFixed(2)}×</span>
      <span className="text-ink-mute text-[11px]"> ({group.n})</span>
    </span>
  );
  if (group.n === 0) return content;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="cursor-help underline decoration-dotted decoration-ink-mute underline-offset-4">{content}</span>
      </TooltipTrigger>
      <TooltipContent side="left"><GroupList group={group} fmt={fmt} age={age} /></TooltipContent>
    </Tooltip>
  );
}

